import React, { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useData } from "../../context/DataContext";
import { useNavigation } from "../../context/NavigationContext";
import { createAdvance, cancelAdvance } from "../../api/advances";
import { uploadAttachments } from "../../api/attachments";
import {
  useNotifications,
  createNotification,
  NOTIFICATION_TYPES,
} from "../../context/NotificationsContext";
import {
  getRequestStatusLabel,
  isOpenRequest,
} from "../../utils/requestHelpers";
import { buildResubmitDraft } from "../../utils/requestTransparency";
import AttachmentUploader from "../common/AttachmentUploader";
import AttachmentList from "../common/AttachmentList";
import RequestTransparency from "../common/RequestTransparency";
import "./AdvanceRequestForm.css";

function AdvanceRequestForm() {
  const { user } = useAuth();
  const { pendingRequests, addPendingRequest, addRecentActivity, findRoleEmployee } =
    useData();
  const { addNotification, addNotificationToUser } = useNotifications();
  const navigation = useNavigation();

  const [amount, setAmount] = useState("");
  const [isExceptional, setIsExceptional] = useState(false);
  const [installments, setInstallments] = useState("1");
  const [guarantorName, setGuarantorName] = useState("");
  const [reason, setReason] = useState("");
  const [files, setFiles] = useState([]);
  const [createdId, setCreatedId] = useState(null);
  const [listRefresh, setListRefresh] = useState(0);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const existingPendingAdvance = pendingRequests.find((req) => {
    const isOwn =
      (user?.id && req.userId && req.userId === user.id) ||
      Boolean(user?.code && req.employeeCode && req.employeeCode === user.code);
    return (
      isOwn &&
      (req.type === "سلفة" || req.type === "advance") &&
      isOpenRequest(req)
    );
  });

  const lastRejectedAdvance = [...pendingRequests]
    .filter((req) => {
      const isOwn =
        (user?.id && req.userId && req.userId === user.id) ||
        Boolean(user?.code && req.employeeCode && req.employeeCode === user.code);
      return (
        isOwn &&
        (req.type === "سلفة" || req.type === "advance") &&
        req.status === "rejected"
      );
    })
    .sort((a, b) =>
      String(b.submittedAt || "").localeCompare(String(a.submittedAt || "")),
    )[0];

  function applyAdvanceDraft(draft) {
    if (!draft || draft.kind !== "advance") return;
    setAmount(draft.amount || "");
    const inst = Number(draft.installments || 1);
    setIsExceptional(inst > 1);
    setInstallments(String(inst || 1));
    setGuarantorName(draft.guarantorName || "");
    setReason(draft.reason || "");
  }

  useEffect(() => {
    const draft = navigation?.requestDraft;
    if (!draft || draft.kind !== "advance") return undefined;
    applyAdvanceDraft(draft);
    navigation.clearRequestDraft?.();
    return undefined;
  }, [navigation?.requestDraft]);

  function handleExceptionalToggle(e) {
    const checked = e.target.checked;
    setIsExceptional(checked);
    if (!checked) {
      setInstallments("1");
      setGuarantorName("");
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setSuccess(false);

    if (existingPendingAdvance) {
      setError("لا يمكن تقديم طلب سلفة جديد قبل الرد على طلبك الحالي");
      return;
    }

    if (!amount || Number(amount) <= 0) {
      setError("من فضلك أدخل قيمة سلفة صحيحة");
      return;
    }
    if (
      isExceptional &&
      (Number(installments) < 1 || Number(installments) > 6)
    ) {
      setError("عدد الأقساط للسلفة الاستثنائية لا يمكن أن يتجاوز 6 أقساط");
      return;
    }
    if (isExceptional && !guarantorName.trim()) {
      setError("السلفة الاستثنائية تتطلب اسم ضامن من الشركة");
      return;
    }
    if (submitting) return;

    setSubmitting(true);
    try {
      const created = await createAdvance({
        amount: Number(amount),
        installments: isExceptional ? Number(installments) : 1,
        reason: reason
          ? isExceptional
            ? `${reason} | ضامن: ${guarantorName.trim()}`
            : reason
          : isExceptional
            ? `سلفة استثنائية | ضامن: ${guarantorName.trim()}`
            : undefined,
      });

      if (files.length && created?.id) {
        await uploadAttachments("advance", created.id, files);
        setFiles([]);
        setCreatedId(created.id);
        setListRefresh((n) => n + 1);
      } else if (created?.id) {
        setCreatedId(created.id);
      }

      await addPendingRequest();

      const hrEmployee = findRoleEmployee("hr");
      if (hrEmployee?.id) {
        const hrNotif = createNotification({
          recipientId: hrEmployee.id,
          recipientRole: "hr",
          type: NOTIFICATION_TYPES.NEW_REQUEST,
          title: "📋 طلب سلفة بانتظار موافقة الموارد البشرية",
          message: `${user?.name} قدّم طلب سلفة بقيمة ${amount} ج.م — بانتظار موافقتك`,
          relatedRequestId: created?.id || null,
          senderName: user?.name || "موظف",
          senderRole: user?.role,
          requestType: "سلفة",
          requestStatus: "pending",
        });
        addNotification(hrNotif);
        addNotificationToUser(hrEmployee.id, hrNotif);
      }

      addRecentActivity({
        id: Date.now(),
        text: `طلب سلفة من ${user?.name || "موظف"} بقيمة ${amount} ج.م`,
        date: new Date().toLocaleDateString("ar-EG"),
        type: "advance",
      });

      setSuccess(true);
    } catch (err) {
      const message = err.response?.data?.message;
      setError(
        Array.isArray(message)
          ? message.join("\n")
          : message || "تعذر إرسال طلب السلفة — تأكد من إعداد سير العمل في النظام",
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function handleCancelPending() {
    if (!existingPendingAdvance?.id) return;
    if (
      !window.confirm(
        "هل تريد إلغاء طلب السلفة الحالي؟ يمكنك تقديم طلب جديد بعد الإلغاء.",
      )
    ) {
      return;
    }

    setCancelling(true);
    setError("");
    try {
      await cancelAdvance(existingPendingAdvance.id);
      await addPendingRequest();
    } catch (err) {
      const message = err.response?.data?.message;
      setError(
        Array.isArray(message)
          ? message.join("\n")
          : message || "تعذر إلغاء طلب السلفة",
      );
    } finally {
      setCancelling(false);
    }
  }

  if (existingPendingAdvance) {
    return (
      <div className="advance-form-card">
        <div className="advance-pending-box">
          <div className="advance-pending-icon">⏳</div>
          <p className="advance-pending-title">لديك طلب سلفة قيد المراجعة</p>
          <p className="advance-pending-subtitle">
            لا يمكن تقديم طلب جديد قبل الرد على طلبك الحالي — أو يمكنك إلغاءه
          </p>

          <div className="advance-pending-details">
            <div className="advance-pending-row">
              <span className="advance-pending-label">قيمة السلفة:</span>
              <span className="advance-pending-value">
                {existingPendingAdvance.amount} ج.م
              </span>
            </div>
            <div className="advance-pending-row">
              <span className="advance-pending-label">عدد الأقساط:</span>
              <span className="advance-pending-value">
                {existingPendingAdvance.installments} أقساط
              </span>
            </div>
            <div className="advance-pending-row">
              <span className="advance-pending-label">تاريخ التقديم:</span>
              <span className="advance-pending-value">
                {existingPendingAdvance.submittedAt}
              </span>
            </div>
            <div className="advance-pending-row">
              <span className="advance-pending-label">الحالة:</span>
              <span className="advance-pending-status">
                {getRequestStatusLabel(existingPendingAdvance.status)}
              </span>
            </div>
          </div>

          <RequestTransparency request={existingPendingAdvance} />

          <div className="advance-pending-note">
            💡 سيتم إشعارك فور رد الموارد البشرية على طلبك
          </div>

          {error && <p className="advance-error">{error}</p>}

          <button
            type="button"
            className="advance-cancel-btn"
            disabled={cancelling}
            onClick={handleCancelPending}
          >
            {cancelling ? "جاري الإلغاء..." : "إلغاء الطلب"}
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="advance-form-card">
        <div className="advance-success-box">
          <p className="advance-success-title">تم إرسال طلب السلفة بنجاح</p>
          <p className="advance-success-subtitle">
            تم إرسال الطلب إلى الموارد البشرية للمراجعة، وسيتم إشعارك فور الموافقة
            أو الرفض.
          </p>
          <p className="advance-success-subtitle">
            لا يمكن تقديم طلب آخر قبل الرد على هذا الطلب.
          </p>
        </div>
      </div>
    );
  }

  return (
    <form className="advance-form-card" onSubmit={handleSubmit} dir="rtl">
      {lastRejectedAdvance ? (
        <RequestTransparency
          request={lastRejectedAdvance}
          compact
          onResubmit={(req) => applyAdvanceDraft(buildResubmitDraft(req))}
        />
      ) : null}
      <div className="advance-field">
        <label className="advance-label">قيمة السلفة المطلوبة (ج.م)</label>
        <input
          type="number"
          min="1"
          className="advance-input"
          placeholder="مثال: 1500"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />
      </div>

      <div className="advance-toggle-row">
        <label className="advance-checkbox-label">
          <input
            type="checkbox"
            className="advance-checkbox"
            checked={isExceptional}
            onChange={handleExceptionalToggle}
          />
          <span>سلفة استثنائية (يمكن تقسيطها)</span>
        </label>
      </div>

      {isExceptional && (
        <>
          <div className="advance-field">
            <label className="advance-label">
              عدد الأقساط (حد أقصى 6 أقساط)
            </label>
            <select
              className="advance-select"
              value={installments}
              onChange={(e) => setInstallments(e.target.value)}
            >
              <option value="1">قسط واحد</option>
              <option value="2">قسطان</option>
              <option value="3">3 أقساط</option>
              <option value="4">4 أقساط</option>
              <option value="5">5 أقساط</option>
              <option value="6">6 أقساط</option>
            </select>
          </div>

          <div className="advance-field">
            <label className="advance-label">اسم الضامن (موظف من الشركة)</label>
            <input
              type="text"
              className="advance-input"
              placeholder="اسم الموظف الضامن"
              value={guarantorName}
              onChange={(e) => setGuarantorName(e.target.value)}
            />
          </div>
        </>
      )}

      <div className="advance-field">
        <label className="advance-label">سبب طلب السلفة (اختياري)</label>
        <textarea
          className="advance-textarea"
          rows={3}
          placeholder="اكتب سبب الطلب إن وجد"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </div>

      <AttachmentUploader
        entityType="advance"
        files={files}
        onFilesChange={setFiles}
      />
      {createdId && (
        <AttachmentList
          entityType="advance"
          entityId={createdId}
          refreshKey={listRefresh}
        />
      )}

      {error && <p className="advance-error">{error}</p>}

      <div className="advance-footer-note">
        سيتم إرسال هذا الطلب مباشرة إلى الموارد البشرية للمراجعة والموافقة.
      </div>

      <button type="submit" className="advance-submit-btn" disabled={submitting}>
        {submitting ? "جاري الإرسال..." : "إرسال الطلب"}
      </button>
    </form>
  );
}

export default AdvanceRequestForm;
